import {css, Typography, useTheme} from '@mui/material';
import {asQueryStrings, GetStylesFunction, mixins} from '@wallaby-ui/core';
import * as React from 'react';
import {useParams} from 'react-router-dom';

import {MerieSchema} from '../../../common/types';
import {TooltipWrapper} from 'src/js/common';
import {EntityLink} from 'src/js/containers/file/components/EntityLink';
import {getHospitalizationDocumentLink, RISK_FACTOR_LABEL_COLORS} from 'src/js/containers/hospitalizations/hospitalizations_helpers';

type PharmaRecommendation = Omit<MerieSchema.components['schemas']['PharmaRecommendationEventDataDto'], 'annotation'>;

interface OwnProps {
  versionSuffix: string;
  encounterId: number | undefined;
  documentVersionId?: number;
  pharmaRecommendation?: PharmaRecommendation;
  highlightType?: string; // enum
  tooltip?: string | JSX.Element;
  isDiscontinued: boolean | undefined;
  onPhRecAnnotationClicked?: (pharmaRecommendation: PharmaRecommendation) => void;
}

type Props = OwnProps;

export const TimelineAnnotation: React.FC<Props> = (props) => {
  const {
    encounterId,
    documentVersionId,
    pharmaRecommendation,
    versionSuffix,
    highlightType,
    tooltip,
    isDiscontinued,
    children,
    onPhRecAnnotationClicked,
  } = props;
  const theme = useTheme();
  const {hospitalizationId} = useParams();

  const backgroundColor = highlightType ? RISK_FACTOR_LABEL_COLORS[highlightType].color : 'transparent';
  const styles = getStyles({theme, params: {backgroundColor}});

  let route: string;
  let searchParams: Record<string, unknown>;

  if (hospitalizationId && pharmaRecommendation) {
    route = window.location.pathname;
    searchParams = {
      rightTabs: 1,
      phRecItem: {openedItemId: pharmaRecommendation.pharmaRecommendationId},
    };
  } else if (hospitalizationId && documentVersionId) {
    route = getHospitalizationDocumentLink(hospitalizationId, documentVersionId, versionSuffix);
    searchParams = {
      leftTabs: {left: 1, reuseDocsPage: false},
      documents: {
        hospitalizationsOfOrigin: [encounterId],
      },
    };
  } else {
    route = '';
    searchParams = {};
  }
  const onAnnotationClicked = React.useCallback(() => {
    if (hospitalizationId && pharmaRecommendation && onPhRecAnnotationClicked) {
      onPhRecAnnotationClicked(pharmaRecommendation);
    }
  }, [hospitalizationId, pharmaRecommendation, onPhRecAnnotationClicked]);

  const textWrapperStyle = isDiscontinued
    ? {
        color: 'var(--dlThemeGray-400)',
      }
    : {};
  return (
    <Typography css={styles.wrapper} variant={'value'}>
      {route ? (
        <div css={styles.link}>
          <TooltipWrapper tooltip={tooltip} maxWidth={300}>
            <EntityLink
              route={route}
              searchParams={asQueryStrings(searchParams)}
              inputCss={styles.text}
              data-discontinued={isDiscontinued}
              suppressPadding
              onClick={onAnnotationClicked}
            >
              {children}
            </EntityLink>
          </TooltipWrapper>
        </div>
      ) : (
        <span css={styles.text}>
          <TooltipWrapper tooltip={tooltip} maxWidth={300} wrapperStyle={textWrapperStyle}>
            {children}
          </TooltipWrapper>
        </span>
      )}
    </Typography>
  );
};

TimelineAnnotation.displayName = 'TimelineAnnotation';

const getStyles: GetStylesFunction = (options) => ({
  wrapper: css`
    line-height: unset;
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: 0;
    max-width: 100%;
  `,
  link: css`
    text-decoration: underline;
    min-width: 0;
    height: 26px;
    ${mixins.dlTextOverflowEllipses};
    span,
    a {
      height: 26px;
      ${mixins.dlTextOverflowEllipses};
    }
  `,
  text: css`
    color: ${options?.theme?.palette.custom.coolGray['800']};
    background-color: ${options?.params?.backgroundColor};
    height: 26px;
    min-width: 0;
    ${mixins.dlTextOverflowEllipses};

    span,
    a {
      color: ${options?.theme?.palette.custom.coolGray['800']};
      background-color: ${options?.params?.backgroundColor};
      height: 26px;
      ${mixins.dlTextOverflowEllipses};
    }
    &[data-discontinued='true'] a {
      color: var(--dlThemeGray-400);
    }
  `,
});
